/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import {
    ICheckoutDeliveryOption,
    ICheckoutDeliveryOptionEdit,
    ICheckoutDeliveryOptionsError,
    ICheckoutDeliveryOptionsList,
    ICheckoutDeliveryOptionsViewProps
} from '@msdyn365-commerce-modules/checkout/src/modules/checkout-delivery-options/./checkout-delivery-options';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import * as React from 'react';
import { ICheckoutDeliveryOptionsResources } from '../definition-extensions/checkout-delivery-options.ext.props.autogenerated';

export interface ICheckoutDeliveryOptionsListC extends ICheckoutDeliveryOptionsList {
    resources: ICheckoutDeliveryOptionsResources;
}
export interface ICheckoutDeliveryOptionsViewPropsC extends ICheckoutDeliveryOptionsViewProps {
    resources: ICheckoutDeliveryOptionsResources;
}

const DeliveryOptionList: React.FC<ICheckoutDeliveryOptionsListC> = ({ DeliveryOptionsList, list, resources }) => {
    if (!list || list.length === 0) {
        return null;
    }
    Array.from(document.getElementsByClassName('ms-checkout-delivery-options__description')).forEach(el => {
        const deliveryOption = el.textContent;
        if (deliveryOption) {
            const position = deliveryOption?.indexOf('(');
            if (position > 0) {
                const output = [deliveryOption.slice(0, position), '<br/>', deliveryOption.slice(position)].join('');
                // tslint:disable-next-line:no-inner-html
                el.innerHTML = output;
            }
        }
    });
    const pickUpList:
        | { code: string; radioButton: React.ReactNode; description: string; price: React.ReactNode }[]
        | ICheckoutDeliveryOptionEdit[] = [];
    // @ts-ignore

    return (
        <Node {...DeliveryOptionsList}>
            <h4>{resources.courierDeliveryTitle}</h4>
            {list.map((deliveryOption: ICheckoutDeliveryOptionEdit) => {
                if (deliveryOption.code !== '04') {
                    return (
                        <Node {...deliveryOption.DeliveryOption} key={deliveryOption.code}>
                            {deliveryOption.radioButton}
                            {deliveryOption.description}
                            {deliveryOption.price}
                        </Node>
                    );
                } else {
                    // @ts-ignore
                    pickUpList.push(deliveryOption);
                    return;
                }
            })}
            {pickUpList.length > 0 && (
                <>
                    <h4>Pick Up in store</h4>
                    {/* @ts-ignore */}
                    <Node {...pickUpList[0].DeliveryOption} key={pickUpList[0].code}>
                        {pickUpList[0]?.radioButton}
                        {pickUpList[0]?.description}
                        {pickUpList[0]?.price}
                    </Node>
                </>
            )}
        </Node>
    );
};

const DeliveryOptionSelected: React.FC<ICheckoutDeliveryOption> = ({ DeliveryOption, description, price }) => {
    return (
        <Node {...DeliveryOption}>
            {description}
            {price}
        </Node>
    );
};

const ErrorMessage: React.FC<ICheckoutDeliveryOptionsError> = ({ Error, title, message }) => {
    return (
        <Node {...Error}>
            {title}
            {message}
        </Node>
    );
};

const CheckoutDeliveryOptionsView: React.FC<ICheckoutDeliveryOptionsViewPropsC> = props => {
    const {
        CheckoutDeliveryOptions,
        viewState,
        deliveryOptions,
        deliveryOptionSelected,
        errorMessage,
        waiting,
        saveButton,
        editButton,
        cancelButton,
        resources
    } = props;
    // @ts-ignore
    const extraTextField = props.config.paragraph;
    const extraText = extraTextField && <Msdyn365.RichTextComponent text={extraTextField} className='ms-checkout__extratext' />;
    return (
        // TODO: All wrapper div should be provided by viewProps. Once SDK provide the sln, update markup.
        <Module {...CheckoutDeliveryOptions}>
            {extraText}
            {viewState.isLoading && waiting}
            {viewState.isError && errorMessage && <ErrorMessage {...errorMessage} />}
            {viewState.isShowList && deliveryOptions && <DeliveryOptionList {...deliveryOptions} resources={resources} />}
            {viewState.isShowSelected && deliveryOptionSelected && <DeliveryOptionSelected {...deliveryOptionSelected} />}
            {viewState.isShowSaveButton && saveButton}
            {viewState.isShowEditButton && editButton}
            {viewState.isShowCancelButton && cancelButton}
        </Module>
    );
};

export default CheckoutDeliveryOptionsView;
